import React, { useState, useEffect, useRef } from "react";
import { getImageUrlWithToken } from "../../utils/ImageUtils";
import {
  Box,
  Typography,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Chip,
  Stack,
  Button,
  Menu,
  MenuItem,
  useTheme,
  ListItemIcon,
  Badge,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import {
  getAllNotifications,
  actionOnNotification,
  getUnreadNotificationCount,
} from "../../services/Notification";
import { timeAgo } from "../../utils/TimeFormat";
import { useNavigate } from "react-router-dom";
import { useMessageContext } from "../../context/MessageProvider";
import ReadMoreText from "../../utils/ReadMoretext";
import { NotificationsActive } from "@mui/icons-material";

export default function NotificationMenu({ user }) {
  const theme = useTheme();
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [activeType, setActiveType] = useState("all");
  const [visibleNotifications, setVisibleNotifications] = useState(5);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const { data } = useMessageContext();
  const navigate = useNavigate();
  const pendingNavRef = useRef(null);

  const menuClick = (event) => {
    setNotificationAnchorEl(event.currentTarget);
    setNotificationCount(0); // Clear badge count when menu is opened
  };

  const getUnreadNotification = () => {
    getUnreadNotificationCount()
      .then((response) => {
        const count = response.unread_notifications_count;
        setNotificationCount(count);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    if (user) {
      getUnreadNotification();
    }
    if (data?.data?.unread_notifications_count) {
      const newNotificationCount = data.data.unread_notifications_count;
      setNotificationCount((prev) => {
        const newTotal = prev + newNotificationCount;
        return newTotal;
      });
    }
  }, [user, data]);



  // ----------fetching notifications from the server----------
  const fetchNotifications = async (type, limit, offset) => {
    setLoading(true);
    try {
      const response = await getAllNotifications(type, limit, offset);
      console.log("Notifications fetched:", response);
      if (offset === 0) {
        setNotifications(response.notifications);
        // Count unread notifications
        const unreadCount = response.notifications.filter(
          (notif) => notif.is_read === 0
        ).length;
        setNotificationCount(unreadCount);
      } else {
        setNotifications((prev) => [...prev, ...response.notifications]);
        // Count unread notifications including loaded more
        const unreadCount = [
          ...notifications,
          ...response.notifications,
        ].filter((notif) => notif.is_read === 0).length;
        setNotificationCount(unreadCount);
      }
      // console.log("newNotification", response.notifications);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications(activeType, 5, 0);
  }, [activeType]);

  const handleClick = (type) => {
    setActiveType(type);
    setVisibleNotifications(5); // Reset visible notifications on filter change
    fetchNotifications(type, 5, 0);
  };

  const loadMoreNotifications = () => {
    const newOffset = notifications.length;
    setVisibleNotifications((prev) => prev + 5);
    fetchNotifications(activeType, 5, newOffset);
  };

  // ----------filtering notifications based on activeType----------

  const filteredNotifications =
    activeType === "all"
      ? notifications
      : activeType === "unread"
        ? notifications.filter((notif) => notif.is_read === 0)
        : notifications.filter((notif) => notif.is_read === 1);

  // ----------handling menu open and close----------
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // ----------handle bulk notification actions----------
  const handleNotificationAction = async (action) => {
    setAnchorEl(null);
    try {
      await actionOnNotification(null, { action });
      // Refresh notifications after action
      fetchNotifications(activeType, 5, 0);
    } catch (error) {
      console.error(error);
    }
  };

  // ----------redirecting to the notification link----------
  const handleRedirect = async (url, id) => {
    setNotificationAnchorEl(null);
    try {
      await actionOnNotification(id);
      navigate(url);
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // ----------handle see more button click----------
  const handleSeeMore = () => {
    pendingNavRef.current = "/notification";
    setNotificationAnchorEl(null);
  };

  const handleMenuExited = () => {
    if (pendingNavRef.current) {
      navigate(pendingNavRef.current);
      pendingNavRef.current = null;
    }
  };

  return (
    <React.Fragment>

      <Badge badgeContent={notificationCount}
        color="error"
        overlap="circular"
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <IconButton sx={{ backgroundColor: theme.palette.action.hover }}
          onClick={menuClick}
        >
          <NotificationsActive />
        </IconButton>
      </Badge>

      <Menu
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={() => setNotificationAnchorEl(null)}
        TransitionProps={{ onExited: handleMenuExited }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              borderRadius: 4,
              maxWidth: 340,
              mt: 1.5,
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6">Notifications</Typography>
          <IconButton onClick={handleMenuClick}>
            <MoreHorizIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => handleNotificationAction("read_all")}>
              Mark all as read
            </MenuItem>
            <MenuItem onClick={() => handleNotificationAction("delete_all")}>
              Clear all
            </MenuItem>
          </Menu>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            px: 2,
            py: 1,
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={1} flexGrow={1}>
            <Chip
              label="All"
              onClick={() => handleClick("all")}
              variant={activeType === "all" ? "filled" : "outlined"}
            />
            <Chip
              label="Unread"
              onClick={() => handleClick("unread")}
              variant={activeType === "unread" ? "filled" : "outlined"}
            />
            <Chip
              label="Read"
              onClick={() => handleClick("read")}
              variant={activeType === "read" ? "filled" : "outlined"}
            />
          </Stack>

          <Button
            size="small"
            sx={{
              textTransform: "none",
              color: theme.palette.primary.light
            }}
            onClick={handleSeeMore}
          >
            See more
          </Button>
        </Stack>

        <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
          {filteredNotifications.map((notification) => (
            <MenuItem key={notification.id}
              selected={notification.is_read === 0}
              onClick={() =>
                handleRedirect(notification.redirect_link, notification.id)
              }
            >
              <ListItem>
                {notification.notification_type !== "new_feature" && (
                  <ListItemAvatar sx={{ minWidth: 0, mr: 2 }}>
                    <Avatar
                      variant="rounded"
                      src={getImageUrlWithToken(notification.icon)}
                    />
                  </ListItemAvatar>
                )}
                <ListItemText
                  primary={
                    <span>
                      <ReadMoreText
                        text={notification.content}
                        maxLength={270}
                        useHtml={true}
                      />
                    </span>
                  }
                  secondary={timeAgo(notification.created_at)}
                />
                {!notification.is_read && (
                  <ListItemIcon sx={{ minWidth: 0, justifyContent: "end" }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                      }}
                    />
                  </ListItemIcon>
                )}
              </ListItem>
            </MenuItem>
          ))}
        </Box>

        {visibleNotifications <= filteredNotifications.length && (
          <Box sx={{ px: 2, py: 1, mt: 1 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={loadMoreNotifications}
              sx={{ textTransform: "none", borderRadius: 4 }}
            >
              Load more
            </Button>
          </Box>
        )}

      </Menu>

    </React.Fragment>
  );
};


